import { useDebounceFn } from "@vueuse/core";
import { i18n } from "~vue/i18n";
import { buildClassifier, type ClassifyResult, type PatternEntry, PII_ALLOW, PII_BLOCK, PII_WARN, type Span } from "~vue/utils/pii/keywordClassifier";
import { computed, type ComputedRef, type Ref, ref, watch } from "vue";

export interface PiiDetection {
    piiResult: Ref<ClassifyResult | null>;
    piiBlocked: ComputedRef<boolean>;
    piiWarning: ComputedRef<boolean>;
    piiDetected: ComputedRef<boolean>;
    acceptRisk: () => void;
    classifyNow: () => void;
    piiBannerMessage: ComputedRef<string>;
    piiMessage: ComputedRef<string>;
    warnSpans: ComputedRef<Span[]>;
    errorSpans: ComputedRef<Span[]>;
}

const DEBOUNCE_MS = 300;

export function usePiiDetection(patterns: PatternEntry[] | null | undefined, textContent: Ref<string>): PiiDetection {
    const piiResult = ref<ClassifyResult | null>(null);
    const riskAccepted = ref(false);
    let previousReason = "";

    // Build classifier once if patterns are provided
    const classify = patterns?.length ? buildClassifier(patterns) : null;

    const classifyNow = () => {
        if (!classify || !textContent.value.trim()) {
            piiResult.value = null;
            return;
        }
        piiResult.value = classify(textContent.value);
    };
    const runClassification = useDebounceFn(classifyNow, DEBOUNCE_MS);

    // Fire-and-forget: result is written to the piiResult ref, so watchers react automatically
    watch(textContent, () => {
        void runClassification();
    });

    // Reset riskAccepted when the detected PII types change
    watch(piiResult, (newResult) => {
        const newReason = newResult?.reason ?? "";
        if (newReason !== previousReason) {
            riskAccepted.value = false;
            previousReason = newReason;
        }
    });

    const piiBlocked = computed(() => piiResult.value?.score === PII_BLOCK);
    const piiWarning = computed(() => piiResult.value?.score === PII_WARN && !riskAccepted.value);
    const piiDetected = computed(() => (piiResult.value?.score ?? PII_ALLOW) > PII_ALLOW);

    // Informative banner shown while typing (no action buttons)
    const piiBannerMessage = computed(() => {
        if (!piiResult.value || piiResult.value.score === PII_ALLOW) {
            return "";
        }
        if (piiResult.value.score === PII_BLOCK) {
            return i18n.global.t("chat.controls.pii.bannerBlocked");
        }
        const reason = piiResult.value.reason;
        if (reason.includes("url")) {
            return i18n.global.t("chat.controls.pii.urlUnavailable");
        }
        return i18n.global.t("chat.controls.pii.bannerWarning");
    });

    // Modal message shown when user attempts to send
    const piiMessage = computed(() => {
        if (!piiResult.value || piiResult.value.score === PII_ALLOW) {
            return "";
        }
        if (piiResult.value.score === PII_BLOCK) {
            return i18n.global.t("chat.controls.pii.dialogBlocked");
        }
        const reason = piiResult.value.reason;
        if (reason.includes("url")) {
            return i18n.global.t("chat.controls.pii.urlUnavailable");
        }
        return i18n.global.t("chat.controls.pii.dialogWarning");
    });

    const warnSpans = computed(() => piiResult.value?.warn_spans ?? []);
    const errorSpans = computed(() => piiResult.value?.error_spans ?? []);

    const acceptRisk = () => {
        riskAccepted.value = true;
    };

    return {
        piiResult,
        piiBlocked,
        piiWarning,
        piiDetected,
        acceptRisk,
        classifyNow,
        piiBannerMessage,
        piiMessage,
        warnSpans,
        errorSpans,
    };
}
